<template>
    <BaseView contentClass="bg-white" contentStyle="padding:0;">
        <template v-slot:top v-if="inspection">
            <InspectionTitleBar
                :title="inspection.checklist_title"
                @cancel="showCancelModal"
                @outline="outlineModalOpened"
            />
            <PageTitleBar
                v-if="hasMultiplePages || hasOnePageWithTitle"
                :page-title="currentPageTitle"
                :current-page="currentPage"
                :total-pages="pageCount"
            />
        </template>

        <div class="inspection" ref="inspectionPage" v-if="inspection">
            <InspectionPage
                :errors="errors"
                :show-errors="showErrors"
                :response-sets="inspection.form.params.response_sets"
                :item="inspectionPages[currentPage]"
                />

            <div class="save-button-container" v-if="lastPage">
                <button
                    class="btn btn-default btn-primary main-button relative"
                    type="button"
                    @click="inspectionSubmitted"
                    >
                        <span :class="{ invisible: processing }">{{ $t("Submit") }}</span>

                        <span
                            v-if="processing"
                            class="absolute"
                            style="top: 50%; left: 50%; transform: translate(-50%, -50%)"
                        >
                            <loader class="text-white" width="32" />
                        </span>
                </button>
            </div>
        </div>


        <template v-slot:bottom v-if="inspection">
            <Pagination
                :has-prev-page="hasPrev"
                :has-next-page="hasNext"
                :prev-page-title="hasPrev ? pageTitleForPage(currentPage - 1) : null"
                :next-page-title="hasNext ? pageTitleForPage(currentPage + 1) : null"
                @prev-page="prev"
                @next-page="next"
            />
        </template>


        <portal to="modals">
            <transition name="fade">
                <CancelModal
                    v-if="$store.state.conduct.modals.cancel.visible"
                    :show-save-draft="!editingInspection"
                    :is-editing-inspection="editingInspection"
                    @saveDraft="saveDraft"
                    @close="closeCancelModal"
                    @cancel="cancel"
                />
            </transition>

            <transition name="fade">
                <NotesModal
                    v-if="$store.state.conduct.modals.notes.visible"
                    :item="$store.state.conduct.modals.notes.item"
                    @save="saveNotes"
                    @close="closeNotesModal"
                />
            </transition>

            <transition name="fade">
                <PhotosModal
                    v-if="$store.state.conduct.modals.photos.visible"
                    :item="$store.state.conduct.modals.photos.item"
                    @save="savePhotos"
                    @close="closePhotosModal"
                />
            </transition>

            <transition name="fade">
                <IssueItemModal
                    v-if="$store.state.conduct.modals.issueItem.visible"
                    :item="$store.state.conduct.modals.issueItem.item"
                    :users="inspection.users"
                    @close="issueItemModalClosed"
                />
            </transition>

            <OutlineModal
                :checklist="inspection"
                v-if="$store.state.conduct.modals.outline.visible"
                @close="outlineModalClosed"
            />

            <ValidationErrorsModal
                v-if="modals.validationErrorsModal"
                @close="modals.validationErrorsModal = false"
                @outline="validationErrorsModalOutlineClicked"
            />
        </portal>

    </BaseView>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import CancelModal from '@mobile/views/ConductInspection/components/Modals/Cancel.vue';
import ValidationErrorsModal from '@mobile/views/ConductInspection/components/Modals/ValidationErrorsModal.vue';
import Loader from '@mobile/components/Loader.vue';
import BaseView from '@mobile/components/BaseView.vue';
import IssueItemModal from '@/views/ConductInspection/Modals/IssueItemModal.vue';
import NotesModal from '@/components/ConductInspection/ConductInspectionModal/ConductInspectionModalNotes.vue';
import PhotosModal from '@/components/ConductInspection/ConductInspectionModal/ConductInspectionModalPhotos.vue';
import OutlineModal from '@/views/ConductInspection/Modals/OutlineModal.vue';
import InspectionPage from '@/views/ConductInspection/Items/Page.vue';
import Pagination from './components/Pagination.vue';
import InspectionTitleBar from './components/InspectionTitleBar.vue';
import PageTitleBar from './components/PageTitleBar.vue';
import { eventBus } from '@/eventBus';

let autoSaveInterval;

export default {
    name: 'ConductInspection',

    components: {
        IssueItemModal,
        CancelModal,
        ValidationErrorsModal,
        NotesModal,
        PhotosModal,
        OutlineModal,
        Loader,
        InspectionPage,
        BaseView,
        Pagination,
        InspectionTitleBar,
        PageTitleBar,
    },

    props: {
        resourceId: {
            required: true,
        },
        type: {
            required: true,
            validator(value) {
                return ['todo', 'checklist', 'draft', 'inspection'].includes(value);
            },
        },
    },

    data: () => ({
        site: null,
        editingInspection: false,
        processing: false,
        uploadStatus: 'success',
        uploadMessage: 'The inspection was submitted!',
        modals: {
            validationErrorsModal: false,
        },
    }),

    watch: {
        editingInspection: {
            handler: 'setMessage',
            immediate: false,
        },
    },

    computed: {
        ...mapGetters({
            inspection: 'conduct/inspection',
            autoSaveEnabled: 'conduct/autoSaveEnabled',
            currentPageHasErrors: 'conduct/currentPageHasErrors',
            currentPage: 'conduct/currentPage',
            showErrors: 'conduct/showErrors',
            errors: 'conduct/errors',
        }),

        hasNext() {
            return this.currentPage < (this.pageCount - 1);
        },

        hasPrev() {
            return this.currentPage > 0;
        },

        pageCount() {
            return this.inspectionPages.length;
        },

        lastPage() {
            return (this.pageCount - 1) === this.currentPage;
        },

        hasMultiplePages() {
            return this.pageCount > 1;
        },

        hasOnePageWithTitle() {
            return this.pageCount === 1 && this.currentPageTitle;
        },

        inspectionPages() {
            return this.inspection.form.items.filter(function (page, index) {
                return index === 0 ? this.shouldShowHeaderPage(page) : true;
            }, this);
        },

        currentPageTitle() {
            if (this.inspectionPages[this.currentPage].params.header === true) {
                return this.$t('Header');
            }
            return this.inspectionPages[this.currentPage].title;
        },

        inspectionHasSites() {
            return this.inspection.sites.length !== 0;
        },
    },

    created() {
        this.$store.commit('conduct/reset');
        this.load();
        this.startAutosaving();
    },

    destroyed() {
        this.stopAutosaving();
    },

    methods: {
        setMessage() {
            this.uploadMessage = this.editingInspection
                ? this.$t('The inspection was updated!')
                : this.$t('The inspection was submitted!');
        },

        ...mapActions({
            issueItemModalClosed: 'conduct/issueItemModalClosed',
            outlineModalClosed: 'conduct/outlineModalClosed',
            outlineModalOpened: 'conduct/outlineModalOpened',
        }),

        startAutosaving() {
            if (!this.editingInspection) {
                autoSaveInterval = setInterval(this.autoSave, 5000);
            }
        },

        stopAutosaving() {
            if (autoSaveInterval) {
                clearInterval(autoSaveInterval);
            }
        },

        load() {
            this.editingInspection = ['inspection'].includes(this.type);
            const action = `conduct/from${_.capitalize(this.type)}`;
            this.$store
                .dispatch(action, this.resourceId)
                .then(() => {
                    console.log('loaded');
                    eventBus.$emit('loaded');
                }).catch((error) => {
                    eventBus.$emit('client-error', error);
                });
        },

        async inspectionSubmitted() {
            if (this.processing) return;

            this.processing = true;
            this.$store
                .dispatch('conduct/inspectionSubmitted')
                .then(() => {
                    this.$toasted.show(this.uploadMessage, { type: this.uploadStatus });
                    this.$router.push({ name: 'todos' });
                }, () => {
                    this.modals.validationErrorsModal = true;
                })
                .finally(() => {
                    this.processing = false;
                });
        },

        pageTitleForPage(pageNumber) {
            if (this.inspectionPages[pageNumber].params.header) {
                return this.$t('Header');
            } if (this.inspectionPages[pageNumber].title) {
                return this.inspectionPages[pageNumber].title;
            }
            return this.$t('Page {page}', { page: pageNumber + 1 });
        },

        autoSave() {
            if (!this.autoSaveEnabled) return; // in case this is an edit
            if (!this.inspection) return; // in case the timer ticks but we still don't have an inspection loaded
            this.$store.dispatch('conduct/saveDraft');
        },

        async saveDraft() {
            await this.$store.dispatch('conduct/saveDraft');

            this.$toasted.show(
                this.$t('The inspection draft was saved!'),
                { type: 'success' },
            );

            this.$router.push({
                name: 'todos',
            });
        },

        saveNotes(notes) {
            this.$store.commit('conduct/saveNotes', notes);
        },

        savePhotos(photos) {
            this.$store.commit('conduct/savePhotos', photos);
        },

        showCancelModal() {
            this.$store.commit('conduct/showCancelModal');
        },

        closeCancelModal() {
            this.$store.commit('conduct/closeCancelModal');
        },

        closeNotesModal() {
            this.$store.commit('conduct/closeNotesModal');
        },

        closePhotosModal() {
            this.$store.commit('conduct/closePhotosModal');
        },

        async cancel() {
            if (!this.editingInspection) {
                await this.$store.dispatch('conduct/cancel');

                this.$toasted.show(
                    this.$t('The inspection was cancelled!'),
                    { type: 'success' },
                );
            }
            this.goBack();
        },

        goBack() {
            this.$store.commit('conduct/reset');
            this.$router.go(-1);
        },

        prev() {
            this.$store.commit('conduct/prevPage');
            eventBus.$emit('pageFlip');
        },

        next() {
            this.$store.commit('conduct/nextPage');
            eventBus.$emit('pageFlip');
        },

        shouldShowHeaderPage(headerPage) {
            for (let i = 0; i < headerPage.items.length; i += 1) {
                if (typeof (headerPage.items[i].params) === 'undefined'
                    || typeof (headerPage.items[i].params.automatic) === 'undefined'
                    || headerPage.items[i].params.automatic === false) {
                    return true;
                }
            }

            return this.inspectionHasSites;
        },

        validationErrorsModalOutlineClicked() {
            this.modals.validationErrorsModal = false;
            this.outlineModalOpened();
        },
    },
};
</script>
